/**
 * Video utilities for extracting metadata from video URLs
 * Supports YouTube, TikTok, and Vimeo
 */

export type VideoProvider = 'youtube' | 'tiktok' | 'vimeo';

export interface VideoMetadata {
  provider: VideoProvider;
  videoId: string;
  url: string;
  thumbnail?: string;
  title?: string;
  duration?: number;
}

/**
 * Extract YouTube video ID from various YouTube URL formats
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Regular YouTube patterns
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Extract TikTok video ID from TikTok URL
 */
export function extractTikTokVideoId(url: string): string | null {
  if (!url) return null;

  // TikTok URL patterns: https://www.tiktok.com/@username/video/1234567890
  // or https://vm.tiktok.com/xxxxx/
  const patterns = [
    /tiktok\.com\/@[^\/]+\/video\/(\d+)/,
    /tiktok\.com\/.*\/video\/(\d+)/,
    /vm\.tiktok\.com\/([a-zA-Z0-9]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Extract Vimeo video ID from Vimeo URL
 */
export function extractVimeoVideoId(url: string): string | null {
  if (!url) return null;

  // Vimeo URL patterns: https://vimeo.com/123456789
  const pattern = /vimeo\.com\/(\d+)/;
  const match = url.match(pattern);

  return match && match[1] ? match[1] : null;
}

/**
 * Detect video provider from URL
 */
export function detectVideoProvider(url: string): VideoProvider | null {
  if (!url) return null;

  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return 'youtube';
  }

  if (lowerUrl.includes('tiktok.com') || lowerUrl.includes('vm.tiktok.com')) {
    return 'tiktok';
  }

  if (lowerUrl.includes('vimeo.com')) {
    return 'vimeo';
  }

  return null;
}

/**
 * Extract video metadata from URL
 */
export function extractVideoMetadata(url: string): VideoMetadata | null {
  if (!url) return null;

  const provider = detectVideoProvider(url);
  if (!provider) return null;

  let videoId: string | null = null;

  switch (provider) {
    case 'youtube':
      videoId = extractYouTubeVideoId(url);
      break;
    case 'tiktok':
      videoId = extractTikTokVideoId(url);
      break;
    case 'vimeo':
      videoId = extractVimeoVideoId(url);
      break;
  }

  if (!videoId) return null;

  // Generate thumbnail URLs
  let thumbnail: string | undefined;
  if (provider === 'youtube') {
    thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  } else if (provider === 'tiktok') {
    // TikTok thumbnails require API access, but we can use a placeholder
    // The backend should fetch this via oEmbed
    thumbnail = undefined;
  } else if (provider === 'vimeo') {
    // Vimeo thumbnails require API access
    thumbnail = undefined;
  }

  return {
    provider,
    videoId,
    url,
    thumbnail,
  };
}

/**
 * Get YouTube embed URL
 */
export function getYouTubeEmbedUrl(videoId: string, autoplay: boolean = false): string {
  return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=${autoplay ? 1 : 0}&modestbranding=1&rel=0`;
}

/**
 * Get TikTok embed URL (TikTok doesn't support direct embedding like YouTube)
 * Returns the original URL instead
 */
export function getTikTokEmbedUrl(videoId: string): string {
  // TikTok videos cannot be embedded directly - must redirect to TikTok
  // Return the video URL that will open in TikTok app or browser
  return `https://www.tiktok.com/video/${videoId}`;
}

/**
 * Get Vimeo embed URL
 */
export function getVimeoEmbedUrl(videoId: string, autoplay: boolean = false): string {
  return `https://player.vimeo.com/video/${videoId}?autoplay=${autoplay ? 1 : 0}&title=0&byline=0&portrait=0`;
}

/**
 * Check if a URL is a valid video URL
 */
export function isValidVideoUrl(url: string): boolean {
  return !!extractVideoMetadata(url);
}












